import { useEffect, useMemo, useState } from 'react';

// Column fitting for Grid and Table (HD-10): a list never scrolls sideways on a narrow screen. When the columns' widths add
// up to more than the list's own width, columns leave until the rest fit.
//   Order: the columns' own `drop` numbers when any column has one (1 leaves first; columns without one always stay, as
//   Storage sets them); otherwise from the right, never the first column, never a column marked `keep`.
//   When: fit === true always; fit === 'auto' (the default) at <= 1024 px only, where the sidebar is a rail or a drawer and
//   the desktop layout no longer holds. At desktop width a Grid keeps every column: DESIGN §2.6 keeps resizable columns.
export const NARROW = '(max-width: 1024px)';
export const FALLBACK_COL = 96;   // the width a Table column without its own counts for

function useMatch(query) {
  const [hit, setHit] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query); const on = () => setHit(m.matches);
    m.addEventListener('change', on); return () => m.removeEventListener('change', on);
  }, [query]);
  return hit;
}

// Whether fitting is on for this list, and the list's own width (clientWidth of `ref`, kept current) while it is.
export function useFitWidth(ref, fit) {
  const narrow = useMatch(NARROW);
  const active = fit === true || (fit === 'auto' && narrow);
  const [avail, setAvail] = useState(null);
  useEffect(() => {
    const el = ref.current;
    if (!active || !el || typeof ResizeObserver === 'undefined') { setAvail(null); return undefined; }
    const ro = new ResizeObserver(() => setAvail(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [active, ref]);
  return active ? avail : null;
}

// The indices of the columns to leave out so the shown widths fit `avail` (null: fitting is off or not measured yet).
export function useDroppedColumns(columns, widths, avail) {
  return useMemo(() => {
    const out = new Set();
    if (avail == null) return out;
    const live = columns.map((c, i) => ({ c, i })).filter(({ c }) => !c.hide);
    let sum = live.reduce((a, { i }) => a + widths[i], 0);
    const explicit = live.some(({ c }) => c.drop);
    const order = explicit
      ? live.filter(({ c }) => c.drop).sort((a, b) => a.c.drop - b.c.drop)
      : live.slice(1).filter(({ c }) => !c.keep).reverse();
    for (const { i } of order) { if (sum <= avail) break; out.add(i); sum -= widths[i]; }
    return out;
  }, [columns, widths, avail]);
}
